import React from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { toggleSidebar, toggleDarkMode } from '../../features/ui/uiSlice';
import { logout } from '../../features/auth/authSlice';
import { fetchUnreadCount } from '../../features/notifications/notificationSlice';
import { isUser, isCompany, isAdmin } from '../../features/auth/authSlice';
import {
    Bars3Icon,
    SunIcon,
    MoonIcon,
    BellIcon,
    ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const Header: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { darkMode } = useAppSelector((state) => state.ui);
    const { user } = useAppSelector((state) => state.auth);
    const { unreadCount, isLoading } = useAppSelector((state) => state.notifications);

    const handleLogout = async () => {
        try {
            await dispatch(logout()).unwrap();
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handleNotificationClick = () => {
        if (!user) return 'U';
        if (isUser(user)) {
            navigate('/dashboard/notifications');
        } else {
            navigate('/notifications');
        }
    };

    React.useEffect(() => {
        if (user && !isLoading) {
            dispatch(fetchUnreadCount());
        }
    }, [dispatch, user, isLoading]);

    const getUserDisplayName = () => {
        if (!user) return 'User';
        if (isUser(user)) return `${user.firstName} ${user.lastName}`;
        if (isCompany(user)) return user.companyName;
        if (isAdmin(user)) return `${user.firstName} ${user.lastName}`;
        return 'User';
    };

    const getUserInitial = () => {
        if (!user) return 'U';
        if (isUser(user)) return user.firstName?.charAt(0) || 'U';
        if (isCompany(user)) return user.companyName?.charAt(0) || 'C';
        if (isAdmin(user)) return user.firstName?.charAt(0) || 'A';
        return 'U';
    };

    const getUserRole = () => {
        if (!user) return 'User';
        if (isUser(user)) return user.designation || 'User';
        if (isCompany(user)) return 'Company';
        if (isAdmin(user)) return 'Administrator';
        return 'User';
    };

    return (
        <header className="sticky top-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-lg border-b border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center justify-between h-20 px-8">
                <div className="flex items-center">
                    <button
                        onClick={() => dispatch(toggleSidebar())}
                        className="p-3 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300 lg:hidden"
                    >
                        <Bars3Icon className="h-6 w-6" />
                    </button>

                    <div className="ml-6 lg:ml-0">
                        <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                            {user && isAdmin(user) ? 'Admin Dashboard' :
                                user && isCompany(user) ? 'Company Dashboard' :
                                    'Dashboard'}
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mt-1">
                            {user && isAdmin(user) ? '' :
                                user && isCompany(user) ? 'Manage your business operations' :
                                    'Your professional workspace'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    {/* Dark mode toggle */}
                    <button
                        onClick={() => dispatch(toggleDarkMode())}
                        className="p-3 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300 hover:scale-105"
                        title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                    >
                        {darkMode ? (
                            <SunIcon className="h-6 w-6" />
                        ) : (
                            <MoonIcon className="h-6 w-6" />
                        )}
                    </button>

                    {/* Notifications */}
                    <button
                        onClick={handleNotificationClick}
                        className="relative p-3 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300 hover:scale-105"
                        title="Notifications"
                    >
                        <BellIcon className="h-6 w-6" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 h-6 w-6 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-lg animate-pulse">
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>

                    {/* Profile section */}
                    <div className="flex items-center space-x-4 p-2 rounded-2xl bg-gray-50 dark:bg-gray-800/50">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                            <span className="text-sm font-bold text-white">
                                {getUserInitial()}
                            </span>
                        </div>
                        <div className="hidden md:block text-left">
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                                {getUserDisplayName()}
                            </p>
                        </div>
                    </div>

                    {/* Logout button */}
                    <button
                        onClick={handleLogout}
                        className="p-3 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-300 hover:scale-105"
                        title="Logout"
                    >
                        <ArrowRightOnRectangleIcon className="h-6 w-6" />
                    </button>
                </div>
            </div>
        </header>
    );
};

export default Header;